import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowLeft, MessageCircle, ClipboardList, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { PageHero, KpiCard, Section } from "@/components/AnalyticsCommon";

export default function SurveyResultsPage() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/surveys/${sid}/results`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [sid]);

  if (loading) return <Skeleton className="h-[600px]" />;
  if (!data) return <div className="p-6 text-center text-muted-foreground">Resultados não encontrados.</div>;

  const s = data.survey;
  const t = data.totals;

  return (
    <div className="space-y-5 pb-10" data-testid="survey-results-page">
      <PageHero
        kicker={<><BarChart3 className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Resultados</>}
        title={s.title}
        description={`${t.responses} resposta(s) · ${s.anonymous ? "anônima" : "identificada"} · ${t.completion_avg}% taxa média de conclusão`}
        actions={
          <Button size="sm" variant="outline" onClick={() => navigate("/pesquisas")}
                  className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                  data-testid="back-to-list">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Voltar
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Respostas" value={t.responses} icon={ClipboardList} accent="emerald" testId="res-total" />
        <KpiCard label="Taxa de conclusão" value={`${t.completion_avg}%`} accent="ochre" testId="res-completion" />
        <KpiCard label="Status" value={s.status} accent={s.status === "publicada" ? "emerald" : "slate"} testId="res-status" />
        <KpiCard label="Categoria" value={s.category || "—"} accent="sky" testId="res-cat" />
      </div>

      <div className="space-y-4">
        {data.questions.map((q, idx) => (
          <QuestionResult key={q.key} index={idx + 1} q={q} />
        ))}
      </div>
    </div>
  );
}

function QuestionResult({ index, q }) {
  return (
    <Section
      kicker={`Pergunta ${index} · ${q.answered}/${q.answered + q.skipped} respondidas`}
      title={q.label}
      testId={`q-result-${q.key}`}
    >
      {q.type === "nps" ? (
        <NpsResult q={q} />
      ) : q.type === "scale_5" || q.type === "scale_10" || q.type === "stars" ? (
        <ScaleResult q={q} />
      ) : q.type === "single" || q.type === "yesno" ? (
        <SingleResult q={q} />
      ) : q.type === "multi" ? (
        <SingleResult q={q} hidePct />
      ) : q.type === "open" ? (
        <OpenResult q={q} />
      ) : (
        <div className="text-[12px] text-muted-foreground">Tipo não suportado.</div>
      )}
    </Section>
  );
}

function NpsResult({ q }) {
  const total = q.answered;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-rose-700">Detratores</div>
          <div className="font-display text-2xl font-semibold text-rose-700 tabular-nums">{q.detractors}</div>
          <div className="text-[10px] text-rose-600">{total ? Math.round(q.detractors / total * 100) : 0}% · 0 a 6</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-amber-700">Neutros</div>
          <div className="font-display text-2xl font-semibold text-amber-700 tabular-nums">{q.passives}</div>
          <div className="text-[10px] text-amber-600">{total ? Math.round(q.passives / total * 100) : 0}% · 7 a 8</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Promotores</div>
          <div className="font-display text-2xl font-semibold text-emerald-700 tabular-nums">{q.promoters}</div>
          <div className="text-[10px] text-emerald-600">{total ? Math.round(q.promoters / total * 100) : 0}% · 9 e 10</div>
        </div>
      </div>
      <div className="rounded-lg bg-gradient-to-br from-[#234935] to-[#1A3528] text-white p-4 text-center">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium">eNPS Score</div>
        <div className={`font-display text-4xl font-bold tabular-nums mt-1 ${
          q.nps_score >= 50 ? "text-emerald-300"
          : q.nps_score >= 0 ? "text-[#C5A059]" : "text-rose-300"
        }`}>
          {q.nps_score >= 0 ? "+" : ""}{q.nps_score}
        </div>
        <div className="text-[10.5px] text-white/60 mt-1">% Promotores − % Detratores</div>
      </div>
      <div className="h-[200px] mt-4">
        <ResponsiveContainer>
          <BarChart data={q.distribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
            <XAxis dataKey="value" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={28} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" fill="#234935" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ScaleResult({ q }) {
  return (
    <div>
      {q.avg !== null && q.avg !== undefined && (
        <div className="rounded-lg bg-[#234935]/5 border border-[#234935]/15 p-3 mb-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-[#8A6B2B]">Média</div>
          <div className="font-display text-3xl font-semibold text-[#234935] tabular-nums">{q.avg}</div>
        </div>
      )}
      <div className="h-[180px]">
        <ResponsiveContainer>
          <BarChart data={q.distribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
            <XAxis dataKey="value" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={28} />
            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" fill="#C5A059" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SingleResult({ q, hidePct }) {
  const max = Math.max(...(q.distribution?.map((d) => d.count) || [1]), 1);
  return (
    <div className="space-y-2">
      {(q.distribution || []).map((d) => {
        const pct = q.answered ? Math.round((d.count / q.answered) * 100) : 0;
        const w = Math.max(4, Math.round((d.count / max) * 100));
        const label = d.option === "sim" ? "Sim" : d.option === "nao" ? "Não" : d.option;
        return (
          <div key={d.option}>
            <div className="flex items-center justify-between text-[11.5px] mb-1">
              <span className="font-medium">{label}</span>
              <span className="font-mono tabular-nums">
                {d.count} {!hidePct && `· ${pct}%`}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-gradient-to-r from-[#234935] to-[#3a7559]"
                   style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OpenResult({ q }) {
  if (!q.sample_responses || q.sample_responses.length === 0) {
    return <div className="text-[12px] text-muted-foreground italic">Sem respostas abertas.</div>;
  }
  return (
    <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
      {q.sample_responses.map((txt, i) => (
        <div key={`r-${i}`} className="flex items-start gap-2 rounded-md bg-slate-50 border border-border p-2.5">
          <MessageCircle className="h-3.5 w-3.5 text-[#C5A059] mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-slate-700 leading-relaxed">{txt}</p>
        </div>
      ))}
    </div>
  );
}
